import type { Properties } from 'hast';
import type * as mdast from 'mdast';

import { VerticalAlignment } from '@gitbook/api-client';
import {
    BLOCK_CODE,
    BLOCK_CODE_DEFAULT_OVERFLOW,
    BLOCK_COLUMN,
    BLOCK_COLUMNS,
    BLOCK_CONTENT_REF,
    BLOCK_DIVIDER,
    BLOCK_DRAWING,
    BLOCK_EMBED,
    BLOCK_EXPANDABLE,
    BLOCK_FILE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_HINT,
    BLOCK_IF,
    BLOCK_IMAGES,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_MATH,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_STEPPER,
    BLOCK_STEPPER_STEP,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    type BlockCodeData,
    type BlockColumnData,
    type BlockColumnsData,
    type BlockEmbedData,
    type BlockImageData,
    type BlockImagesData,
    type BlockOpenAPIOperationData,
    type BlockOpenAPISchemasData,
    type BlockOpenAPIWebhookData,
    type BlockOrderedListData,
    type BlockReusableContentData,
    type BlockTabsData,
    expandable,
    FRAGMENT_CAPTION,
    images,
    lengthToString,
    list,
} from '@gitbook/doc-core';
import { Block } from '@gitbook/slate';

import { getEnumAttribute } from '../../parse/html/utils';
import { formatHTML } from '../html';
import type { FormattingOptions } from '../options';
import { createCustomBlock } from './compiler-custom-blocks';
import { transformIntegrationBlocks } from './transform-blocks-integration';
import { transformSwaggerBlocks } from './transform-blocks-swagger';
import { transformTableBlocks } from './transform-blocks-table';
import type { GastToMdastTransformFn, GastToMdastTransformMap } from './types';

export const transformBlocks: GastToMdastTransformMap<Block> = {
    ...transformSwaggerBlocks,
    ...transformTableBlocks,
    ...transformIntegrationBlocks,
    [BLOCK_OPENAPI_OPERATION]: async (node: Block, _eat, options) => {
        const data = node.data.toObject<BlockOpenAPIOperationData>();

        const resolved = await options.resolveContentRef(data.ref);
        if (!resolved) {
            return [];
        }

        return [
            createCustomBlock(
                BLOCK_OPENAPI_OPERATION,
                {
                    spec: data.ref.spec,
                    path: data.path,
                    method: data.method,
                },
                [
                    {
                        type: 'link',
                        title: undefined,
                        url: resolved.url,
                        children: [
                            {
                                type: 'text',
                                value: resolved.text,
                            },
                        ],
                    },
                ]
            ),
        ];
    },
    [BLOCK_OPENAPI_SCHEMAS]: async (node: Block, _eat, options) => {
        const data = node.data.toObject<BlockOpenAPISchemasData>();

        const resolved = await options.resolveContentRef(data.ref);
        if (!resolved) {
            return [];
        }

        return [
            createCustomBlock(
                BLOCK_OPENAPI_SCHEMAS,
                {
                    spec: data.ref.spec,
                    schemas: data.schemas,
                    grouped: data.grouped,
                },
                [
                    {
                        type: 'link',
                        title: undefined,
                        url: resolved.url,
                        children: [
                            {
                                type: 'text',
                                value: resolved.text,
                            },
                        ],
                    },
                ]
            ),
        ];
    },
    [BLOCK_OPENAPI_WEBHOOK]: async (node: Block, _eat, options) => {
        const data = node.data.toObject<BlockOpenAPIWebhookData>();

        const resolved = await options.resolveContentRef(data.ref);
        if (!resolved) {
            return [];
        }

        return [
            createCustomBlock(
                BLOCK_OPENAPI_WEBHOOK,
                {
                    spec: data.ref.spec,
                    name: data.name,
                    method: data.method,
                },
                [
                    {
                        type: 'link',
                        title: undefined,
                        url: resolved.url,
                        children: [
                            {
                                type: 'text',
                                value: resolved.text,
                            },
                        ],
                    },
                ]
            ),
        ];
    },
    [BLOCK_PARAGRAPH]: async (node: Block, eat, options) => {
        const align = node.data.get('align');
        if (align && align !== 'start') {
            return [
                {
                    type: 'html',
                    value: await formatHTML(node, options),
                },
            ];
        }
        return [
            {
                type: 'paragraph',
                children: (await eat(node.nodes)) as mdast.PhrasingContent[],
            },
        ];
    },
    [BLOCK_HEADING_1]: createHeadingTransform(1),
    [BLOCK_HEADING_2]: createHeadingTransform(2),
    [BLOCK_HEADING_3]: createHeadingTransform(3),
    [BLOCK_DIVIDER]: async () => {
        return [{ type: 'thematicBreak', children: [] }];
    },
    [BLOCK_QUOTE]: async (node: Block, eat) => {
        return [
            {
                type: 'blockquote',
                children: (await eat(node.nodes)) as mdast.BlockContent[],
            },
        ];
    },
    [BLOCK_LIST_OL]: async (node, eat) => {
        const data = node.data.toFrozenObject<BlockOrderedListData>();
        return [
            {
                type: 'list',
                ordered: true,
                start: data.start || 1,
                spread: false,
                children: (await eat(node.nodes)) as mdast.ListContent[],
            },
        ];
    },
    [BLOCK_LIST_UL]: async (node, eat) => {
        return [
            {
                type: 'list',
                ordered: false,
                spread: false,
                children: (await eat(node.nodes)) as mdast.ListContent[],
            },
        ];
    },
    [BLOCK_LIST_TASKS]: async (node, eat) => {
        return [
            {
                type: 'list',
                ordered: false,
                spread: false,
                children: (await eat(node.nodes)) as mdast.ListContent[],
            },
        ];
    },
    [BLOCK_LIST_ITEM]: async (node, eat, _options, parent) => {
        const spread =
            node.nodes
                .reverse()
                .skipWhile((node) => list.isList(node))
                .reverse().size > 1;

        const isInTasksList = parent.object === 'block' && parent.type === BLOCK_LIST_TASKS;

        return [
            {
                type: 'listItem',
                spread,
                checked: isInTasksList ? node.data.get('checked', false) : null,
                children: (await eat(node.nodes)) as mdast.BlockContent[],
            },
        ];
    },
    [BLOCK_CODE]: async (node, _eat, context) => {
        const data = node.data.toFrozenObject<BlockCodeData>();

        // When we use advanced features in code blocks: highlighting or annotations/expressions
        // we format the code block as HTML.
        const shouldUseHTML = node.nodes.some((line) => {
            const lineData = line.data.toFrozenObject();

            if (lineData.highlighted) {
                return true;
            }
            if (line.nodes.some((child) => !child.isText())) {
                return true;
            }

            return false;
        });

        if (shouldUseHTML) {
            const codeAsHTML = await formatHTML(node, context);

            return [
                {
                    type: 'html',
                    value: codeAsHTML,
                },
            ];
        }

        const text = node.nodes.reduce((str, child, index) => {
            return `${str}${child.text}${index < node.nodes.size - 1 ? '\n' : ''}`;
        }, '');

        const code: mdast.Code = {
            type: 'code',
            lang: node.data.get('syntax') || null,
            meta: null,
            value: text,
        };

        const customBlockData = {
            ...(typeof data.title === 'string' ? { title: data.title } : {}),
            ...((data.overflow || BLOCK_CODE_DEFAULT_OVERFLOW) !== BLOCK_CODE_DEFAULT_OVERFLOW
                ? { overflow: data.overflow }
                : {}),
            ...(data.lineNumbers ? { lineNumbers: 'true' } : {}),
            ...(typeof data.fullWidth !== 'undefined'
                ? { fullWidth: `${Boolean(data.fullWidth)}` }
                : undefined),
            ...(typeof data.expandable !== 'undefined'
                ? { expandable: `${Boolean(data.expandable)}` }
                : undefined),
            ...(typeof data.collapsedLineCount !== 'undefined' &&
            Number.isInteger(data.collapsedLineCount) &&
            data.collapsedLineCount > 0
                ? { collapsedlinecount: `${data.collapsedLineCount}` }
                : undefined),
        };

        if (Object.keys(customBlockData).length > 0) {
            return [createCustomBlock('code', customBlockData, [code])];
        }
        return [code];
    },
    [BLOCK_EMBED]: async (node: Block, eat) => {
        const caption = node.fragments.get(FRAGMENT_CAPTION);
        const hasCaption = caption && !caption.isEmpty;
        const data = node.data.toFrozenObject<BlockEmbedData>();

        return [
            createCustomBlock(
                BLOCK_EMBED,
                {
                    url: data.url,
                    ...(typeof data.fullWidth !== 'undefined'
                        ? { fullWidth: `${Boolean(data.fullWidth)}` }
                        : undefined),
                },
                hasCaption ? await eat(caption.nodes) : []
            ),
        ];
    },
    [BLOCK_HINT]: async (node: Block, eat) => {
        return [
            createCustomBlock(
                BLOCK_HINT,
                {
                    style: node.data.get('style'),
                },
                await eat(node.nodes)
            ),
        ];
    },
    [BLOCK_IF]: async (node: Block, eat) => {
        return [
            createCustomBlock(
                BLOCK_IF,
                { expression: node.data.get('expression') },
                await eat(node.nodes)
            ),
        ];
    },
    [BLOCK_COLUMNS]: async (node, eat) => {
        const data = node.data.toFrozenObject<BlockColumnsData>();
        return [
            createCustomBlock(
                BLOCK_COLUMNS,
                {
                    ...(typeof data.fullWidth !== 'undefined'
                        ? { fullWidth: `${Boolean(data.fullWidth)}` }
                        : undefined),
                },
                await eat(node.nodes)
            ),
        ];
    },
    [BLOCK_COLUMN]: async (node, eat) => {
        const data = node.data.toFrozenObject<BlockColumnData>();
        return [
            createCustomBlock(
                BLOCK_COLUMN,
                {
                    ...(typeof data.width !== 'undefined'
                        ? { width: lengthToString(data.width) }
                        : undefined),
                    ...(typeof data.verticalAlignment !== 'undefined'
                        ? {
                              valign: getEnumAttribute<VerticalAlignment>(
                                  data as Properties,
                                  'verticalAlignment',
                                  [
                                      VerticalAlignment.Top,
                                      VerticalAlignment.Middle,
                                      VerticalAlignment.Bottom,
                                  ],
                                  undefined
                              ),
                          }
                        : undefined),
                },
                await eat(node.nodes)
            ),
        ];
    },
    [BLOCK_TABS]: async (node, eat) => {
        const data = node.data.toFrozenObject<BlockTabsData>();
        return [
            createCustomBlock(
                BLOCK_TABS,
                {
                    ...(typeof data.fullWidth !== 'undefined'
                        ? { fullWidth: `${Boolean(data.fullWidth)}` }
                        : undefined),
                },
                await eat(node.nodes)
            ),
        ];
    },
    [BLOCK_TABS_ITEM]: async (node, eat) => {
        const title = node.data.get('title');
        return [createCustomBlock('tab', { title }, await eat(node.nodes))];
    },
    [BLOCK_STEPPER]: async (node, eat) => {
        return [createCustomBlock('stepper', {}, await eat(node.nodes))];
    },
    [BLOCK_STEPPER_STEP]: async (node, eat) => {
        return [createCustomBlock('step', {}, await eat(node.nodes))];
    },
    // @ts-expect-error
    [BLOCK_IMAGES]: async (node, _eat, options) => {
        const data = node.data.toFrozenObject<BlockImagesData>();
        const children: mdast.PhrasingContent[] = [];

        const alignProp = typeof data.align === 'string' ? ` align="${data.align}"` : '';
        const fullWidthProp =
            typeof data.fullWidth !== 'undefined'
                ? ` data-full-width="${Boolean(data.fullWidth)}"`
                : '';
        const withFrameProp =
            typeof data.withFrame !== 'undefined'
                ? ` data-with-frame="${Boolean(data.withFrame)}"`
                : '';

        /**
         * Loop over the nodes and see if we have atleast one node that will be formatted as HTML,
         * in that case we need to format all nodes as "HTML" and wrap the children under a div, OR, if we
         * have an align property, OR full width property, then we need to wrap the
         * children under a div and ensure  nodes are formatted as HTML.
         */
        const formatAsHTML =
            !!alignProp ||
            !!fullWidthProp ||
            !!withFrameProp ||
            node.nodes.some((imageNode) => {
                if (!Block.isBlock(imageNode)) {
                    return false;
                }

                const imageData = imageNode.data.toFrozenObject() as BlockImageData;
                if (imageData.refDark) {
                    // Image with dark alternative can't be represented in Markdown
                    return true;
                }

                if (typeof images.getImageAltText(imageNode) !== 'undefined') {
                    // Image with an alt text are now only represented as HTML
                    // Only legacy block images with only a caption are represented as Markdown
                    return true;
                }

                return false;
            });

        for (let index = 0; index < node.nodes.size; index++) {
            const n = node.nodes.get(index);

            if (!Block.isBlock(n)) {
                continue;
            }

            if (formatAsHTML) {
                children.push({
                    type: 'html',
                    value: await formatHTML(n, options),
                });
            } else {
                children.push(...(await createMdImage(n, options)));
            }

            // Use an empty space between each Node, forcing the
            // stringify to create inline nodes.
            if (index < node.nodes.size - 1) {
                children.push({
                    type: 'text',
                    value: ' ',
                });
            }
        }

        const withDivHtml =
            children.length > 1 || !!alignProp || !!fullWidthProp || !!withFrameProp;
        return [
            {
                type: 'paragraph',
                children: formatAsHTML
                    ? [
                          ...(withDivHtml
                              ? [
                                    {
                                        type: 'html',
                                        value: `<div${alignProp}${fullWidthProp}${withFrameProp}>`,
                                    },
                                ]
                              : []),
                          ...children,
                          ...(withDivHtml ? [{ type: 'html', value: '</div>' }] : []),
                      ]
                    : children,
            },
        ];
    },

    [BLOCK_MATH]: (node) => {
        const formula = node.data.get('formula').trim();
        if (!formula) {
            return [];
        }

        return [
            {
                type: 'math',
                value: formula,
            },
        ];
    },
    [BLOCK_FILE]: async (node, eat, options) => {
        const caption = node.fragments.get(FRAGMENT_CAPTION);
        const resolved = await options.resolveContentRef(node.data.get('ref'));
        if (!resolved) {
            return [];
        }
        return [
            createCustomBlock(
                BLOCK_FILE,
                {
                    src: resolved.url,
                },
                caption && !caption.isEmpty ? await eat(caption.nodes) : []
            ),
        ];
    },
    [BLOCK_DRAWING]: async (node, _eat, options) => {
        const htmlImg = await formatHTML(node, options);

        return [
            {
                type: 'html',
                value: htmlImg,
            },
        ];
    },
    [BLOCK_CONTENT_REF]: async (node, _eat, options) => {
        const resolved = await options.resolveContentRef(node.data.get('ref'));
        if (!resolved) {
            return [];
        }
        return [
            createCustomBlock(
                BLOCK_CONTENT_REF,
                {
                    url: resolved.url,
                },
                [
                    {
                        type: 'paragraph',
                        children: [
                            {
                                type: 'link',
                                url: resolved.url,
                                children: [
                                    {
                                        type: 'text',
                                        value: resolved.text,
                                    },
                                ],
                            },
                        ],
                    },
                ]
            ),
        ];
    },
    [BLOCK_REUSABLE_CONTENT]: async (node, _eat, options) => {
        const { ref } = node.data.toFrozenObject() as BlockReusableContentData;
        const resolved = await options.resolveContentRef(ref);

        if (!resolved) {
            return [];
        }

        return [
            createCustomBlock(
                'include',
                {
                    '0': resolved.url,
                },
                []
            ),
        ];
    },
    [BLOCK_EXPANDABLE]: async (node, eat, options) => {
        const title = expandable.getTitleFragment(node);
        const body = expandable.getBodyFragment(node);

        const titleAsHTML = await formatHTML(title, options);

        return [
            {
                type: 'html',
                value: '<details>',
            },
            {
                type: 'html',
                value: `<summary>${titleAsHTML.replace(/(^<p>)|(<\/p>$)/g, '')}</summary>`,
            },
            ...(await eat(body.nodes)),
            {
                type: 'html',
                value: '</details>',
            },
        ];
    },
};

/**
 * Create a heading transform.
 */
function createHeadingTransform(
    depth: number
): GastToMdastTransformFn<Block, mdast.Heading | mdast.Html, mdast.PhrasingContent | mdast.Html> {
    return async (node, eat, options) => {
        const id = node.data.get('id', '');
        const align = node.data.get('align');
        if (align && align !== 'start') {
            return [
                {
                    type: 'html',
                    value: await formatHTML(node, options),
                },
            ];
        }
        return [
            {
                type: 'heading',
                depth: (depth +
                    (!node.data.get('isMarkdownPageTitle') && options.headingsOffset
                        ? options.headingsOffset
                        : 0)) as mdast.Heading['depth'],
                children: [
                    ...(await eat(node.nodes)),

                    ...(id
                        ? [
                              { type: 'text', value: ' ' } as mdast.Text,
                              {
                                  type: 'html',
                                  value: `<a href="#${id}" id="${id}"></a>`,
                              } as mdast.HTML,
                          ]
                        : []),
                ],
            },
        ];
    };
}

/**
 * Create a markdown image node.
 */
async function createMdImage(node: Block, options: FormattingOptions): Promise<mdast.Image[]> {
    const caption = node.fragments.get(FRAGMENT_CAPTION);

    const imageData = node.data.toFrozenObject() as BlockImageData;

    const resolved = await options.resolveContentRef(imageData.ref);
    if (!resolved) {
        return [];
    }

    const alt = images.getImageAltText(node);

    const imgMd: mdast.Image = {
        type: 'image',
        url: resolved.url,
        alt: alt || '',
    };

    if (typeof alt === 'undefined') {
        imgMd.alt = caption?.text || '';
    }

    return [imgMd];
}
